'use client';

import React, { useEffect, useRef } from 'react';
import Image from 'next/image';

export default function FinalCTA() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-visible');
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.2 }
    );
    if (ref?.current) observer?.observe(ref?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="portfolio" className="py-16 sm:py-24 px-4 sm:px-6 relative overflow-hidden">
      <span id="demo" className="block -translate-y-20" aria-hidden />
      {/* Background glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 60% 80% at 50% 50%, rgba(124,58,237,0.1) 0%, transparent 70%)',
        }}
      />
      <div
        ref={ref}
        className="reveal-blur max-w-6xl mx-auto"
      >
        {/* Full-width bento cell */}
        <div className="bento-card card-shine finalcta-card relative p-6 sm:p-12 lg:p-16 overflow-hidden">
          {/* Decorative violet glow orbs */}
          <div
            className="absolute -top-20 -right-20 w-80 h-80 rounded-full pointer-events-none"
            style={{
              background: 'radial-gradient(circle, rgba(124,58,237,0.15) 0%, transparent 70%)',
              filter: 'blur(40px)',
            }}
          />
          <div
            className="absolute -bottom-20 -left-20 w-60 h-60 rounded-full pointer-events-none"
            style={{
              background: 'radial-gradient(circle, rgba(124,58,237,0.1) 0%, transparent 70%)',
              filter: 'blur(40px)',
            }}
          />

          {/* Dot grid */}
          <div
            className="absolute inset-0 pointer-events-none opacity-10"
            style={{
              backgroundImage: 'radial-gradient(rgba(167,139,250,0.5) 1px, transparent 1px)',
              backgroundSize: '32px 32px',
            }}
          />

          <div className="relative z-10 grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Copy */}
            <div className="flex flex-col gap-6">
              <div className="inline-flex items-center gap-2 w-fit px-4 py-1.5 rounded-full border border-[rgba(124,58,237,0.3)]">
                <span className="w-1.5 h-1.5 rounded-full bg-violet-light status-pulse" />
                <span className="text-[10px] font-mono text-violet-light tracking-widest uppercase">Free 14-day trial · No credit card</span>
              </div>

              <h2
                className="font-manrope font-extrabold tracking-tighter text-[#EEEEF0] leading-tight"
                style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)' }}
              >
                Your portfolio, live in
                <span
                  style={{
                    background: 'linear-gradient(135deg, #7C3AED, #A78BFA)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                > 15 minutes.</span>
              </h2>

              <p className="text-[#9999A8] text-lg leading-relaxed max-w-lg">
                We seed your demo environment with sample properties so you see real data on day one — not an empty screen. No setup required. Cancel anytime.
              </p>

              {/* Trust signals */}
              <div className="flex flex-wrap gap-4 mt-2">
                {[
                  { icon: '🔒', text: 'SOC 2 Type II certified' },
                  { icon: '⚡', text: 'Setup in under 15 min' },
                  { icon: '📞', text: 'Onboarding call included' },
                ]?.map((signal) => (
                  <div key={signal?.text} className="flex items-center gap-2 text-sm text-[#9999A8]">
                    <span>{signal?.icon}</span>
                    <span className="font-mono">{signal?.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: CTA block */}
            <div className="flex flex-col gap-5">
              {/* Primary CTA */}
              <a
                href="#demo"
                className="cta-primary flex items-center justify-center gap-3 px-8 py-5 rounded-xl text-lg font-bold tracking-tight shadow-violet-glow-lg"
              >
                See Your Portfolio Live
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </a>

              {/* Secondary CTA */}
              <a
                href="#walkthrough"
                className="cta-secondary flex items-center justify-center gap-3 px-8 py-5 rounded-xl text-base"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <polygon points="10 8 16 12 10 16 10 8" fill="currentColor" />
                </svg>
                Watch the 90-Second Walkthrough
              </a>

              {/* Social proof mini */}
              <div className="flex items-center gap-3 pt-2">
                <div className="flex -space-x-2">
                  {[
                    'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=40&q=80',
                    'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=40&q=80',
                    'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=40&q=80',
                  ]?.map((src, i) => (
                    <Image
                      key={i}
                      src={src}
                      alt={`Landlord ${i + 1}`}
                      width={32}
                      height={32}
                      className="w-8 h-8 rounded-full border-2 border-[#1C1C24] object-cover"
                    />
                  ))}
                </div>
                <span className="text-xs font-mono text-[#5A5A6E]">
                  Joined by 2,400+ landlords this year
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}